import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import TextInput from '../NumberTextInput';
import Modal from '../modal/Modal';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { BoldText, DemiLightText, MediumText, RegularText } from '../../lib/Text';
import SvgIcon from '../SvgIcon';
import { colors } from '../../lib/colors';
import { MemberMembershipDataType } from '../../services/useMembershipQueryService';
import { Body2Bold } from '../../lib/font';
import useMembershipPaymentsMutationService from '../../services/useMembershipPaymentsMutationService';
import NewCalendar from '../Calendar/NewCalendar';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { useMembershipTheme } from './useMembershipTheme';
import {
    clearStableSharedAccountingOperation,
    getSharedAccountingErrorMessage,
    getStableSharedAccountingOperation,
    getStableSharedAccountingOperationForSource,
    isReplayedAccountingResponse,
    REPLAYED_ACCOUNTING_ALERT_MESSAGE,
    REPLAYED_ACCOUNTING_ALERT_TITLE,
    SHARED_MEMBERSHIP_SECTION_ID,
    shouldDiscardSharedAccountingOperation,
    SharedMembershipPool,
    sharedMembershipKeys,
    SharePoolScope,
    StableSharedAccountingOperation,
    useSharePoolByScope,
} from '../../features/sharedMembership';
import {
    getOriginalSaleRefundInputError,
    getPaymentInputError,
    getRefundPreview,
    shouldMarkGroupFullRefund,
} from '../../features/sharedMembership/groupRefund';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { getSalesManagerName, RefundCandidate } from '../../features/sharedMembership/refundCandidates';
import { isMemberRecordReadOnly } from '../../features/publicMember/types';

type RefundSuccessContext = {
    hasActiveSharePoolWarning: boolean;
    /**
     * 서버가 같은 요청 번호의 최초 결과를 재생한 응답. 이번 클릭으로는 돈이 움직이지 않았으므로
     * 부모 화면도 "환불 완료" 성공 안내를 띄우면 안 된다.
     */
    isReplayed: boolean;
};

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    membership: MemberMembershipDataType;
    coachName: string;
    memberName: string;
    type: 'upgrade' | 'refund';
    createAlert: any;
    /** 매출 원장에서 진입할 때 서버가 계산한 해당 매출의 환불 가능 금액 */
    maxRefundAmountOverride?: number;
    /** 인계 후에도 환불액이 어느 판매자 매출에서 차감되는지 안내한다. */
    salesAttributionName?: string;
    /** 매출 원장 ID. 전달되면 인계 계보와 원판매자 귀속을 보장하는 전용 API를 사용한다. */
    salesRecordId?: string;
    /**
     * 환불 자체가 불가능한 사유. 있으면 입력을 막고 이 문구만 보여준다.
     * 계보가 끊겼거나 공유 중인 경우처럼 서버가 어차피 거절할 상황을 미리 알린다.
     */
    refundBlockMessage?: string;
    /**
     * 서버가 계산한 환불 후보(결제행). 인계 계보가 있을 때만 내려온다.
     * **어느 행을 고르느냐가 어느 강사·판매담당자의 매출을 취소하느냐**를 결정하므로
     * 여러 개면 사용자가 직접 고른다. 프론트가 임의로 정하지 않는다.
     */
    refundCandidates?: RefundCandidate[];
    onRefundSuccess?: (context: RefundSuccessContext) => void;
};

const ModalRefundAndUpgrade = ({
    setIsVisible,
    membership,
    coachName,
    memberName,
    type,
    createAlert,
    maxRefundAmountOverride,
    salesAttributionName,
    salesRecordId,
    refundBlockMessage,
    refundCandidates,
    onRefundSuccess,
}: Props) => {
    const queryClient = useQueryClient();
    const accountingOperationRef = React.useRef<StableSharedAccountingOperation | null>(null);
    const submissionLockRef = React.useRef(false);
    const membershipId = membership._id;
    const isGroup = membership?.source === 'group';
    const sharePoolScope: SharePoolScope = isGroup
        ? { sourceType: 'MEMBER_TICKET', memberTicketId: membershipId }
        : { sourceType: 'MEMBERSHIP', membershipId };
    const { isSharedMembershipReadEnabled } = useCenterFeatureFlags();
    const sharePoolQuery = useSharePoolByScope({
        scope: sharePoolScope,
        enabled: isSharedMembershipReadEnabled,
    });

    /**
     * 백엔드는 환불에 공유 가드를 두지 않는다(운영 절차로 수용). 환불해도 ACTIVE 풀은
     * 남으므로, 운영자가 정리를 잊고 유령 풀이 쌓이지 않게 환불 직후 종료를 유도한다.
     *
     * 매출 상세처럼 공유 캐시가 없는 진입 경로에서도 같은 query key로 공유 상태를 먼저 조회한다.
     */
    const hasActiveSharePoolAfterRefund = (): boolean => {
        const pool =
            sharePoolQuery.data ??
            queryClient.getQueryData<SharedMembershipPool | null>(sharedMembershipKeys.poolByScope(sharePoolScope));
        return pool?.status === 'ACTIVE';
    };

    const notifyActiveSharePoolAfterRefund = (): boolean => {
        if (!hasActiveSharePoolAfterRefund()) return false;

        createAlert(
            '활성 공유가 남아 있습니다',
            '환불된 수강권에 공유 참여자가 연결되어 있습니다. 참여자의 예약을 확인한 뒤 공유를 종료해주세요.',
            true,
            '확인',
            () =>
                document
                    .getElementById(SHARED_MEMBERSHIP_SECTION_ID)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        );
        return true;
    };

    const { patchMembershipRefundMutation, patchMembershipUpgradeMutation } = useMembershipPaymentsMutationService({
        onPatchMembershipRefundSuccess(result) {
            clearStableSharedAccountingOperation(accountingOperationRef.current);
            accountingOperationRef.current = null;
            //getMembershipForMemberQuery
            queryClient.invalidateQueries({
                queryKey: ['getMembershipForMemberQuery'],
            });
            setIsVisible(false);

            /**
             * 서버가 최초 결과를 재생한 응답이면 이번 클릭으로 환불이 새로 일어나지 않았다.
             * 성공으로 보여주면 관리자가 의도한 2번째 환불이 조용히 삼켜져 이중 환불을 숨기게 된다.
             *
             * 전역 알럿은 하나만 떠서 마지막 호출이 앞의 것을 덮으므로, 공유 풀 안내를 따로
             * 띄우지 않고 한 문구에 합쳐 어느 쪽도 잃지 않게 한다.
             */
            if (isReplayedAccountingResponse(result)) {
                createAlert(
                    REPLAYED_ACCOUNTING_ALERT_TITLE,
                    hasActiveSharePoolAfterRefund()
                        ? `${REPLAYED_ACCOUNTING_ALERT_MESSAGE} 환불된 수강권에 공유 참여자가 남아 있으니 참여자의 예약을 확인한 뒤 공유를 종료해주세요.`
                        : REPLAYED_ACCOUNTING_ALERT_MESSAGE,
                    true,
                );
                onRefundSuccess?.({ hasActiveSharePoolWarning: false, isReplayed: true });
                return;
            }

            const hasActiveSharePoolWarning = notifyActiveSharePoolAfterRefund();
            onRefundSuccess?.({ hasActiveSharePoolWarning, isReplayed: false });
        },
        onPatchMembershipUpgradeSuccess(result) {
            clearStableSharedAccountingOperation(accountingOperationRef.current);
            accountingOperationRef.current = null;
            queryClient.invalidateQueries({
                queryKey: ['getMembershipForMemberQuery'],
            });
            setIsVisible(false);
            // 업그레이드도 같은 operation journal을 쓴다. 재생된 응답을 성공으로 보여주지 않는다.
            if (isReplayedAccountingResponse(result)) {
                createAlert(REPLAYED_ACCOUNTING_ALERT_TITLE, REPLAYED_ACCOUNTING_ALERT_MESSAGE, true);
            }
        },
    });

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    // 환불결제일자 div ref
    const divRef = React.useRef<HTMLDivElement>(null);
    // div 위치
    const [divPosition, setDivPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (divRef.current) {
            const rect = divRef.current.getBoundingClientRect();
            setDivPosition({ top: rect.top + rect.height, left: rect.left });
        }
    }, [divRef]);

    const title = type === 'refund' ? '수강권 환불' : '수강권 업그레이드';
    const isActive = membership?.status === 'active';
    // 카드 헤더 테마 — PT/그룹 공통. BGColor가 없으면 그룹 에메랄드 gradient fallback
    const { headerBackground } = useMembershipTheme(membership);
    // 총회차, 현재회차, 남은회차, 수업단가, 결제금액
    const totalsession = membership?.totalSession;
    const currentSession = membership?.currentSession;
    const remainingSession = membership?.remainSession;
    const pricePerSchedule = membership?.pricePerSchedule;
    // 인계 계보가 있으면 서버가 계산한 후보 중 하나를 고른다. 후보가 하나면 부모가 이미
    // salesRecordId 로 넘겨주므로 그 값이 초기 선택이 된다.
    const candidates = React.useMemo(() => refundCandidates ?? [], [refundCandidates]);
    // 후보가 여럿이면 **비워 둔다.** 첫 후보를 기본 선택하면 사용자가 아무것도 고르지 않고
    // 제출했을 때 가장 오래된 매출이 환불되어 귀속(강사·판매담당자)이 틀어진다.
    const [selectedCandidateId, setSelectedCandidateId] = useState<string>(
        () => salesRecordId || (candidates.length === 1 ? candidates[0]?.membershipPaymentId || '' : ''),
    );
    const selectedCandidate = candidates.find(
        (candidate) => candidate.membershipPaymentId === selectedCandidateId,
    );
    // 후보가 있으면 선택된 후보가 권위값이다. 없으면 기존 경로(매출 상세 / 일반 상세) 그대로.
    const effectiveSalesRecordId = candidates.length > 0 ? selectedCandidateId : salesRecordId;
    /**
     * 결제행 단위 신규 환불(POST /:id/refunds)로 나가는가.
     *
     * 이 경로가 만든 환불행만 서버가 이후 수정·삭제를 막는다(REFUND_ROW_IMMUTABLE).
     * 구 PATCH 환불은 원장이 없어 그 논리가 적용되지 않으므로 같은 경고를 붙이면 거짓말이 된다.
     */
    const isIrreversibleRefund = !!effectiveSalesRecordId;
    const isWithdrawnMember = isMemberRecordReadOnly(membership as any);
    const effectiveAttributionName =
        (selectedCandidate ? getSalesManagerName(selectedCandidate) || selectedCandidate.coachName : '') ||
        salesAttributionName;

    // 인계본은 결제 원장이 원판매 수강권에 남아 자기 값만 보면 상세 헤더와 숫자가 어긋난다.
    // 담당강사만 바뀐 하나의 수강권이므로 표시 기준을 헤더와 맞춘다.
    const chainAmounts = membership as any;
    const hasChainAmounts = chainAmounts?.chainTotalPaymentAmount !== undefined;
    const totalPrice = hasChainAmounts ? chainAmounts.chainTotalPrice : membership?.totalPrice;
    const totalPaymentAmount = hasChainAmounts
        ? Number(chainAmounts.chainTotalPaymentAmount ?? 0) || 0
        : Number(membership?.totalPaymentAmount ?? 0) || 0;
    // 일반 상세는 누적 결제금액, 매출 상세는 서버가 원장 전체로 계산한 환불 가능 금액을 사용한다.
    // 인계 계보가 있으면 선택한 결제행의 서버 계산값을 쓴다. 프론트가 재계산하지 않는다.
    // 음수(이미 과다 환불된 상태)면 0으로 clamp — 더 이상 환불 불가
    const maxRefundAmount =
        type === 'refund'
            ? Math.max(
                  0,
                  selectedCandidate?.refundableAmount ?? maxRefundAmountOverride ?? totalPaymentAmount,
              )
            : Infinity;

    const [session, setSession] = useState(0);
    const [price, setPrice] = useState(0);
    const refundPreview = getRefundPreview({
        refundableAmount: maxRefundAmount,
        remainingSession: Number(remainingSession),
        refundAmount: Number(price),
        refundSession: Number(session),
    });
    // 날짜 선택 UI의 같은 값은 새로고침 뒤에도 같은 accounting payload여야 한다.
    const [date, setDate] = useState(dayjs().startOf('day').toISOString());

    // 환불회차가 남은회차 넘었을때 오류 여부
    const [isSessionError, setIsSessionError] = useState(false);
    // 환불금액이 누적결제금액을 초과하는지
    const [isPriceError, setIsPriceError] = useState(false);

    const sessionTitle = type === 'refund' ? '환불 등록 회차' : '추가 등록 회차';
    const priceTitle = type === 'refund' ? '환불 결제 금액' : '추가 결제 금액';
    const dateTitle = type === 'refund' ? '환불 결제 일자' : '추가 결제 일자';
    const buttonTitle = type === 'refund' ? '환불 설정 완료' : '업그레이드 설정 완료';

    // 그룹 수강권이면 refund-v2 엔드포인트로 분기 (커밋 6bf11df5)
    const centerId = getItemWithExpiration('authObject')?.centerId;

    const onClickConfirm = async () => {
        if (submissionLockRef.current) return;
        // 서버가 어차피 거절할 상황(계보 끊김·공유 중 등)은 요청을 보내지 않는다.
        if (type === 'refund' && refundBlockMessage) {
            createAlert('환불할 수 없습니다', refundBlockMessage, true);
            return;
        }
        // 회차 초과는 이제 입력 단계에서 삼키지 않으므로 여기서 막는다.
        if (type === 'refund' && Number(session) > Number(remainingSession)) {
            createAlert(
                '입력값 확인',
                `환불 회차가 남은 회차(${Number(remainingSession).toLocaleString()}회)를 초과할 수 없습니다.`,
                true,
            );
            return;
        }
        // 어느 매출을 취소하는지가 곧 강사·판매담당자 귀속이다. 고르지 않으면 보내지 않는다.
        if (type === 'refund' && candidates.length > 1 && !selectedCandidateId) {
            createAlert('매출 선택 필요', '어느 매출을 환불할지 먼저 선택해주세요.', true);
            return;
        }
        const parsedSession = Number(session);
        const parsedPrice = Number(price);
        const inputError =
            effectiveSalesRecordId && type === 'refund'
                ? getOriginalSaleRefundInputError(parsedSession, parsedPrice)
                : getPaymentInputError({
                      type,
                      session: parsedSession,
                      amount: parsedPrice,
                  });
        if (inputError) {
            createAlert('입력값 확인', inputError, true);
            return;
        }
        const numericSession = parsedSession;
        const numericPrice = parsedPrice;
        if (type === 'refund' && numericPrice > maxRefundAmount) {
            setIsPriceError(true);
            return;
        }
        const isGroupFullRefund = shouldMarkGroupFullRefund({
            isGroup,
            type,
            session: Number(numericSession),
            remainingSession,
        });
        /**
         * 키에 넣는 필드 = 그 요청 body에 실제로 실려 서버 지문에 들어가는 필드.
         *
         * `isFullRefund`는 그룹 refund-v2 경로에서만 전송된다(원매출 환불 API는 이 필드를
         * 보내지 않으므로 지문에도 없다). 지문에 없는 경로까지 키에 넣으면 그 사용처의
         * 기존 키만 이유 없이 바뀌어 저장된 in-flight 키가 무효화된다.
         */
        const sendsIsFullRefundToServer = !effectiveSalesRecordId && isGroupFullRefund;
        const accountingIntent = {
            type: type === 'refund' ? ('REFUND' as const) : ('UPGRADE' as const),
            membershipId: effectiveSalesRecordId || membershipId,
            session: numericSession,
            amount: numericPrice,
            paymentDate: date,
            ...(sendsIsFullRefundToServer ? { isFullRefund: true } : {}),
        };
        // 전용 원매출 환불 API는 PT/그룹 모두 멱등 키 계약을 사용한다.
        const accountingOperation = effectiveSalesRecordId
            ? getStableSharedAccountingOperation(accountingOperationRef.current, accountingIntent)
            : getStableSharedAccountingOperationForSource(
                  isGroup ? 'group' : 'pt',
                  accountingOperationRef.current,
                  accountingIntent,
              );
        accountingOperationRef.current = accountingOperation || null;

        const param = {
            membershipId,
            ...(effectiveSalesRecordId ? { salesRecordId: effectiveSalesRecordId } : {}),
            session: numericSession,
            amount: numericPrice,
            date,
            ...(accountingOperation ? { operationId: accountingOperation.operationId } : {}),
            ...(isGroup ? { source: 'group' as const, centerId } : {}),
        };
        // 멱등 키가 실린 요청만 "입력값을 바꾸지 말고 다시 시도"가 안전하다.
        // 키 없는 요청(그룹 업그레이드)은 재시도가 곧 이중 처리이므로 확인부터 안내한다.
        const hasIdempotencyKey = !!accountingOperation;
        const lostResponseMessage = hasIdempotencyKey
            ? '응답을 확인하지 못했습니다. 네트워크를 확인한 뒤 입력값을 바꾸지 말고 다시 시도해주세요. 같은 요청 번호로 보내므로 중복 처리되지 않습니다.'
            : '응답을 확인하지 못했습니다. 중복 방지 요청 번호가 없으므로 즉시 재시도하지 말고, 수강권과 결제 이력을 새로고침해 처리 여부를 먼저 확인해주세요.';

        submissionLockRef.current = true;
        try {
            const result =
                type === 'refund'
                    ? await patchMembershipRefundMutation.mutateAsync({
                          ...param,
                          // 0회 입력을 전액 환불로 오인하면 미래 예약까지 일괄 취소된다.
                          ...(isGroupFullRefund ? { isFullRefund: true } : {}),
                      })
                    : await patchMembershipUpgradeMutation.mutateAsync(param);
            if (result?.status !== 200 && result?.status !== 201) {
                /**
                 * 서버 지문과 이 키가 어긋났다는 응답이면 같은 키로는 무엇을 보내도 통과할 수 없다.
                 * 서버 원장에는 TTL이 없어 폐기하지 않으면 프론트 30일 TTL이 지날 때까지 교착이다.
                 *
                 * 폐기만 하고 자동 재시도는 하지 않는다 — 새 키로 나가는 요청은 곧 새 환불이므로
                 * 관리자가 의도를 다시 확인하고 직접 눌러야 한다.
                 */
                if (shouldDiscardSharedAccountingOperation(result)) {
                    clearStableSharedAccountingOperation(accountingOperation);
                    accountingOperationRef.current = null;
                }
                createAlert(
                    '환불·업그레이드 처리 확인',
                    getSharedAccountingErrorMessage(
                        result,
                        result?.networkError
                            ? lostResponseMessage
                            : '처리에 실패했습니다. 입력값을 확인한 뒤 다시 시도해주세요.',
                    ),
                    true,
                );
            }
        } catch {
            createAlert('환불·업그레이드 처리 확인', lostResponseMessage, true);
        } finally {
            submissionLockRef.current = false;
            /**
             * 처리 결과가 불확실한 채로 끝났을 수 있다. 이 상태에서 모달을 닫았다 다시 열면
             * 캐시된 예전 환불 후보(=환불 가능액)가 그대로 보여, 이미 처리된 건을 다시
             * 환불하려 들 수 있다. 서버가 계산한 후보를 다시 읽게 해 화면이 진실을 보게 한다.
             */
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
        }
    };

    return (
        <Modal
            setIsVisible={setIsVisible}
            whiteViewWidth={825}
            borderRadius={25}
            isCloseOutside={false}
            isCloseButton
            modalCommpont={
                isCalendarOpen ? (
                    <NewCalendar
                        date={dayjs(date)}
                        selectedDay={dayjs(date)}
                        setIsCalendar={setIsCalendarOpen}
                        onClickDate={(day) => {
                            setDate(dayjs(day).startOf('day').toISOString());
                        }}
                        isAllDate={false}
                        top={divPosition.top - 500}
                        left={divPosition.left}
                        isNextMonth
                        isPrevMonth
                        titleFormat="YYYY년 MM월"
                    />
                ) : undefined
            }
            onClose={() => {
                setIsVisible(false);
            }}
        >
            <Col className="flex w-full h-full p-[35px]">
                <Row className="flex items-center mb-[57px]">
                    <BoldText fontSize={28} lineHeight={38} color="#3D4244" className="mr-[31px]">
                        {title}
                    </BoldText>
                    <MediumText
                        fontSize={23}
                        lineHeight={38}
                        color="#3D4244"
                        className="mr-[12px]"
                        letterSpacing={-0.3}
                    >
                        {coachName} 강사
                    </MediumText>
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={20}
                        fill={colors.trans}
                        stroke="#3D4244"
                        strokeWidth={1.5}
                    />
                    <MediumText
                        fontSize={23}
                        lineHeight={38}
                        color="#3D4244"
                        className="ml-[12px]"
                        letterSpacing={-0.3}
                    >
                        {memberName} 회원
                    </MediumText>
                </Row>
                {type === 'refund' && salesAttributionName && (
                    <Col className="mb-[28px] rounded-[12px] border border-solid border-[#BFDBFE] bg-[#EFF6FF] px-[18px] py-[14px]">
                        <RegularText fontSize={14} lineHeight={21} color="#1E3A8A" letterSpacing={-0.2}>
                            {`환불 금액은 ${salesAttributionName}님의 환불 발생월 매출에서 차감되고, 환불 회차는 현재 담당 강사의 수강권에 반영됩니다.`}
                        </RegularText>
                    </Col>
                )}
                {/*
                  U7 — 되돌릴 수 없다는 것을 실행 전에 말한다(P13).

                  이 경로로 만들어진 환불행은 서버가 이후 수정·삭제를 409 로 막는다
                  (REFUND_ROW_IMMUTABLE). 정정 수단이 아직 없어 금액이나 회차를 잘못 넣으면
                  운영팀 경유 말고는 방법이 없다. 탈퇴 회원·종료 수강권은 애초에 이 경로로만
                  환불되므로 특히 중요하다.
                */}
                {type === 'refund' && !refundBlockMessage && isIrreversibleRefund && (
                    <Col className="mb-[28px] rounded-[12px] border border-solid border-[#FDBA74] bg-[#FFF7ED] px-[18px] py-[14px]">
                        <RegularText fontSize={14} lineHeight={21} color="#C2410C" letterSpacing={-0.2}>
                            {isWithdrawnMember
                                ? '탈퇴 회원은 원매출 환불만 가능합니다. 환불 내역은 이후 수정·삭제할 수 없으니 금액과 회차를 확인해 주세요.'
                                : '이 환불 내역은 이후 수정·삭제할 수 없습니다. 금액과 회차를 확인해 주세요.'}
                        </RegularText>
                    </Col>
                )}
                <Row className="flex justify-between w-full items-center mb-[51px]">
                    <Col className="flex flex-col w-full items-center">
                        <DemiLightText fontSize={16} lineHeight={22} color="#3D4244" className="mb-[20px]">
                            현재 수강권 정보
                        </DemiLightText>
                        <Col
                            className="flex flex-col w-full rounded-tl-[12px] rounded-tr-[12px] h-[88px]  justify-center items-center "
                            style={{
                                background: isActive ? headerBackground : colors.GREY_200,
                                alignItems: 'center',
                            }}
                        >
                            <Col className="flex w-full h-full pt-[16px] pb-[21px] pl-[24px] justify-between items-start">
                                <Body2Bold className="text-white">{membership?.name || '수업 수강권'}</Body2Bold>
                            </Col>
                        </Col>
                        <Col className="flex border border-solid border-[#E5E5E9] pl-[29px] pr-[21px] py-[31px] justify-between rounded-bl-[12px] rounded-br-[12px] h-[250px] w-full">
                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    총 회차
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {totalsession}회
                                </RegularText>
                            </Row>
                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    현재 회차
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {currentSession}회
                                </RegularText>
                            </Row>
                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    남은 회차
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {remainingSession}회
                                </RegularText>
                            </Row>
                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    수업 단가
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {convertToKRW(pricePerSchedule)}
                                </RegularText>
                            </Row>
                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    판매 금액
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {convertToKRW(totalPrice)}
                                </RegularText>
                            </Row>
                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    결제 금액
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {convertToKRW(totalPaymentAmount)}
                                </RegularText>
                            </Row>
                        </Col>
                    </Col>
                    <Col className="flex flex-col w-[1px] h-[375px] bg-[#D9D9D9] mx-[41px]" />
                    {/* 후보 선택 목록이 들어가면 고정 높이(309px)를 넘겨 버튼이 입력칸을 덮는다.
                        최소 높이만 유지하고 내용에 따라 늘어나게 한다. */}
                    <Col className="flex flex-col w-full min-h-[309px] justify-between gap-[16px]">
                        <Col>
                            {type === 'refund' && refundBlockMessage && (
                                <Col className="mb-[16px] px-[14px] py-[12px] bg-[#FFF7ED] border border-solid border-[#FDBA74] rounded-[8px]">
                                    <RegularText fontSize={13} lineHeight={19} color="#C2410C">
                                        {refundBlockMessage}
                                    </RegularText>
                                </Col>
                            )}
                            {type === 'refund' && candidates.length > 1 && (
                                // 후보가 늘어나도 모달 높이를 밀어내지 않게 목록만 스크롤한다.
                                <Col className="mb-[16px] max-h-[132px] overflow-y-auto shrink-0">
                                    <RegularText fontSize={14} lineHeight={20} color="#505967" className="mb-[8px]">
                                        어느 매출을 환불할지 선택해주세요
                                    </RegularText>
                                    {candidates.map((candidate) => {
                                        const isSelected = candidate.membershipPaymentId === selectedCandidateId;
                                        const attribution =
                                            getSalesManagerName(candidate) || candidate.coachName || '-';
                                        return (
                                            <Col
                                                key={candidate.membershipPaymentId}
                                                onClick={() =>
                                                    setSelectedCandidateId(candidate.membershipPaymentId || '')
                                                }
                                                className={`mb-[6px] px-[12px] py-[10px] rounded-[8px] border border-solid cursor-pointer ${
                                                    isSelected
                                                        ? 'border-primary bg-[#EEF2FF]'
                                                        : 'border-[#E5E7EB] bg-white'
                                                }`}
                                            >
                                                <Row className="justify-between items-center">
                                                    <RegularText fontSize={13} lineHeight={19} color="#3D4244">
                                                        {dayjs(candidate.paymentDate).format('YYYY-MM-DD')} ·{' '}
                                                        {candidate.saleStatus === 'UPGRADED' ? '업그레이드' : '판매'}
                                                    </RegularText>
                                                    <RegularText fontSize={13} lineHeight={19} color="#3D4244">
                                                        {convertToKRW(candidate.refundableAmount ?? 0)}원
                                                    </RegularText>
                                                </Row>
                                                <RegularText fontSize={12} lineHeight={17} color="#6B7280">
                                                    매출 귀속 · {attribution}
                                                </RegularText>
                                            </Col>
                                        );
                                    })}
                                </Col>
                            )}
                            {type === 'refund' && effectiveAttributionName && candidates.length === 1 && (
                                <RegularText fontSize={12} lineHeight={17} color="#6B7280" className="mb-[12px]">
                                    {effectiveAttributionName} 님의 매출에서 차감됩니다
                                </RegularText>
                            )}
                            <Row className="items-center mb-[12px]">
                                <RegularText fontSize={16} lineHeight={23} color="#505967" className=" mr-[12px]">
                                    {sessionTitle}
                                </RegularText>
                                <RegularText fontSize={12} lineHeight={17} color={isSessionError ? 'red' : '#505967'}>
                                    {isSessionError ? '환불회차가 남은회차를 초과할 수 없습니다.' : ''}
                                </RegularText>
                            </Row>
                            <TextInput
                                value={session}
                                onChange={(value) => {
                                    // 초과 입력을 삼키면 타이핑 도중 글자가 사라진 것처럼 보인다.
                                    // 값은 그대로 받고 오류만 표시한 뒤 제출에서 막는다.
                                    setSession(value as number);
                                    setIsSessionError(type === 'refund' && (value as number) > remainingSession);
                                }}
                                unit="회"
                                placeholder="0"
                                width={116}
                                error={isSessionError}
                            />
                            {type === 'refund' && (
                                <RegularText fontSize={12} lineHeight={18} color="#6B7280" className="mt-[7px]">
                                    입력 후 남은 회차 {refundPreview.remainingSessionAfterRefund.toLocaleString()}회
                                </RegularText>
                            )}
                        </Col>
                        <Col>
                            <Row className="items-center mb-[12px]">
                                <RegularText fontSize={16} lineHeight={23} color="#505967" className="mr-[12px]">
                                    {priceTitle}
                                </RegularText>
                                <RegularText fontSize={12} lineHeight={17} color={isPriceError ? 'red' : '#505967'}>
                                    {isPriceError
                                        ? `환불 가능 금액(${(maxRefundAmount ?? 0).toLocaleString()}원)을 초과할 수 없습니다`
                                        : ''}
                                </RegularText>
                            </Row>
                            <TextInput
                                value={price}
                                onChange={(value) => {
                                    const next = Number(value) || 0;
                                    setPrice(next);
                                    if (type === 'refund') {
                                        setIsPriceError(next > maxRefundAmount);
                                    }
                                }}
                                comma
                                unit="원"
                                placeholder="0"
                                width={116}
                                error={isPriceError}
                            />
                            {type === 'refund' && (
                                <RegularText fontSize={12} lineHeight={18} color="#2563EB" className="mt-[7px]">
                                    현재 환불 가능 {convertToKRW(refundPreview.refundableAmount)}원 · 환불 후{' '}
                                    {convertToKRW(refundPreview.remainingAmountAfterRefund)}원
                                </RegularText>
                            )}
                        </Col>
                        <Col ref={divRef} className="flex flex-col">
                            <RegularText fontSize={16} lineHeight={23} color="#505967" className="mb-[12px]">
                                {dateTitle}
                            </RegularText>
                            <Col
                                className={`flex  border  border-solid h-[46px] justify-center rounded-[10px] bg-white px-[18px] cursor-pointer hover:border-primary transition-colors duration-300 ease-in-out ${
                                    isCalendarOpen ? 'border-primary' : 'border-[#D9D9D9B2]'
                                }`}
                                onClick={() => {
                                    setIsCalendarOpen(true);
                                }}
                            >
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {dayjs(date).format('YYYY-MM-DD')}
                                </RegularText>
                            </Col>
                        </Col>
                    </Col>
                </Row>
                <Col
                    className={`flex justify-center items-center self-end w-[214px] h-[71px] bg-primary rounded-[9px] ${
                        patchMembershipRefundMutation.isLoading || patchMembershipUpgradeMutation.isLoading
                            ? 'cursor-wait opacity-60'
                            : 'cursor-pointer'
                    }`}
                    onClick={onClickConfirm}
                >
                    <RegularText fontSize={20} lineHeight={23} color="white">
                        {buttonTitle}
                    </RegularText>
                </Col>
            </Col>
        </Modal>
    );
};
export default ModalRefundAndUpgrade;

